import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidDateParam(value) {
  if (!DATE_PARAM_PATTERN.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export default function useDateRangeParams(defaultStartDate, defaultEndDate) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStartDate = searchParams.get('startDate');
  const rawEndDate = searchParams.get('endDate');
  const hasValidRange =
    isValidDateParam(rawStartDate || '') &&
    isValidDateParam(rawEndDate || '') &&
    rawStartDate <= rawEndDate;
  const startDate = hasValidRange ? rawStartDate : defaultStartDate;
  const endDate = hasValidRange ? rawEndDate : defaultEndDate;

  useEffect(() => {
    if (rawStartDate === startDate && rawEndDate === endDate) return;

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('startDate', startDate);
      nextParams.set('endDate', endDate);
      return nextParams;
    }, { replace: true });
  }, [endDate, rawEndDate, rawStartDate, setSearchParams, startDate]);

  const setDateRange = (nextRange, additionalParams = {}) => {
    const nextStartDate = nextRange?.startDate;
    const nextEndDate = nextRange?.endDate;
    const hasValidNextRange =
      isValidDateParam(nextStartDate || '') &&
      isValidDateParam(nextEndDate || '') &&
      nextStartDate <= nextEndDate;
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('startDate', hasValidNextRange ? nextStartDate : defaultStartDate);
      nextParams.set('endDate', hasValidNextRange ? nextEndDate : defaultEndDate);
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') nextParams.delete(key);
        else nextParams.set(key, value);
      });
      return nextParams;
    }, { replace: true });
  };

  return { startDate, endDate, setDateRange };
}
